import {
  addErrorHandler as addSingleSpaErrorHandler,
  getAppStatus,
  LOAD_ERROR,
} from 'single-spa';
/* eslint-disable import/no-relative-packages */
import {
  getMicroFrontends,
  Locale,
  MicroFrontendMountPoint,
  MondrianInstance,
} from '../../../config';
import { errors } from '../constants/errors';
import { ErrorType } from '../types/errors';

const getLocale = (): Locale => {
  const localeFromLocalStorage = localStorage.getItem(
    'app.core-chosen-locale'
  ) as Locale;

  return Object.values(Locale).includes(localeFromLocalStorage)
    ? localeFromLocalStorage
    : Locale.EN_DE;
};

const getPlatformErrorContainer = () => {
  return document.getElementById('platform-error');
};

const getPlatformContentContainer = () => {
  return document.getElementById('platform-container');
};

const platformMountPoints: MicroFrontendMountPoint[] = [
  MicroFrontendMountPoint.CORE,
  MicroFrontendMountPoint.NAVIGATION,
];

const isPlatformMicroFrontend = (
  instance: MondrianInstance,
  microFrontendName: string
) => {
  return Boolean(
    getMicroFrontends(instance).find(
      (mfe) =>
        mfe.packageName === microFrontendName &&
        platformMountPoints.includes(mfe.mountPoint)
    )
  );
};

/**
 * Generates error HTML for the given error type.
 * @param type
 * TODO: Directly use OXYs error component as webcomponent or use parcels
 */
export const generateErrorHTML = (type: ErrorType) => {
  const { title, body, image, button } = errors[type][getLocale()];

  const buttonTemplate = `
    <p>
      <a 
        href="${button?.url}" 
        class="
          inline-block bg-light-surface-action-primary 
          text-light-content-onaction-inverse 
          hover:bg-light-surface-action-primary-hover 
          active:bg-light-action-active 
          focus:ring focus:ring-offset-2 
          focus:bg-light-surface-action-primary-focus 
          focus:ring-light-action-focus 
          rounded-md h-6 text-md px-2
        "
      >
        <span class="inline-flex h-full items-center space-x-1.5 max-w-full">
          <span class="text-ellipsis overflow-hidden whitespace-nowrap">
            ${button?.text}
          </span>
        </span>
      </a>
    </p>
  `;

  return `
    <div class="flex flex-col gap-3 justify-center items-center h-full w-full">
      <div class="w-11/12 h-auto max-w-[256px] max-h-32" aria-hidden="true">
        ${image}
      </div>
      <h2 class="font-semibold">${title}</h2>
      <div class="flex flex-col gap-2 max-w-[30ch] lg:max-w-[60ch]">
        <p>${body.split('\n').join('</p><p>')}</p>
      </div>
      ${button ? buttonTemplate : ''}
    </div>
  `;
};

/**
 * Loads the @iu/myiu.fe.app.404 application.
 */
export const loadNotFoundApplication = () =>
  Promise.resolve({
    bootstrap: () => Promise.resolve(),
    mount() {
      document.getElementById(
        'single-spa-application:@iu/myiu.fe.app.404'
      ).innerHTML = generateErrorHTML(ErrorType.NOT_FOUND);
      return Promise.resolve();
    },
    unmount: () => Promise.resolve(),
  });

/**
 * Triggers a platform error of the given type.
 * @param type
 */
export const triggerPlatformError = (type: ErrorType) => {
  getPlatformErrorContainer().innerHTML = generateErrorHTML(type);
  getPlatformErrorContainer().classList.remove('hidden');
  getPlatformContentContainer().classList.add('hidden');
};

/**
 * Clears the platform error.
 */
export const clearPlatformError = () => {
  getPlatformErrorContainer().innerHTML = '';
  getPlatformErrorContainer().classList.add('hidden');
  getPlatformContentContainer().classList.remove('hidden');
};

/**
 * Adds a single-spa error handler that handles errors that occur during
 * application loading.
 * @param instance
 */
export const addErrorHandler = (instance: MondrianInstance) =>
  addSingleSpaErrorHandler((error) => {
    if (!error) {
      return;
    }

    // If a module fails to load (for example, due to network error),
    // single-spa will handle the error but SystemJS will not automatically
    // retry to download the module later. To do so, we delete the module from
    // the SystemJS registry and re-attempt to download the module when
    // System.import() on an application in LOAD_ERROR status is called again.
    // https://single-spa.js.org/docs/api/#handling-load_error-status-to-retry-module
    if (getAppStatus(error.appOrParcelName) === LOAD_ERROR) {
      System.delete(System.resolve(error.appOrParcelName));
    }

    // Micro-frontends that are rendered at the navigation or core
    // mount-point are considered part of the platform core and are essential
    // for it to function. if one of these fails to load, we render an error
    // at the platform level.
    if (isPlatformMicroFrontend(instance, error.appOrParcelName)) {
      triggerPlatformError(ErrorType.LOAD);
    }

    // eslint-disable-next-line no-console
    console.error(
      `Error loading micro-frontend "${error.appOrParcelName}": ${getAppStatus(
        error.appOrParcelName
      )}`
    );
  });
